import type { Model } from "@/schemas/model.schema";
import type { FlexComponent } from "@/utils/render-component";
import { effectScope } from "vue";
import BrandLogoSmall from "@/components/features/BrandLogoSmall";
import Zai from "@/icons/svgs/Zai";
import { useCatalogModelsQuery } from "@/queries/models.query";

let _modelsQuery: ReturnType<typeof useCatalogModelsQuery> | null = null;

function getModelsData() {
  if (!_modelsQuery) {
    const scope = effectScope(true);
    _modelsQuery = scope.run(() => useCatalogModelsQuery())!;
  }
  return _modelsQuery.data.value;
}

/**
 * Display lookup: name -> model, for surfaces that RENDER a model already
 * chosen (agent rows, thread headers, context gauges). Never a source of
 * options.
 *
 * It resolves against the FULL catalog on purpose. A model the org cannot run
 * would otherwise resolve to `undefined` exactly where the warning matters
 * most, and the agents table would list an agent that cannot run with no icon
 * and no badge at all.
 */
export function getModelObject(model: string | Model) {
  if (typeof model === "string") {
    return getModelsData()?.find(({ name }) => name === model);
  }

  return model;
}

const iconsByProvider = {
  openrouter: "simple-icons:openrouter",
  "opencode-go": "simple-icons:opencode",
  openai: "simple-icons:openai",
  anthropic: "simple-icons:anthropic",
  qwen: "simple-icons:qwen",
  alibaba: "simple-icons:qwen",
  meta: "simple-icons:meta",
  llama: "simple-icons:meta",
  deepseek: "simple-icons:deepseek",
  google: "simple-icons:google",
  mistral: "simple-icons:mistralai",
  minimax: "simple-icons:minimax",
  moonshot: "simple-icons:kimi",
  nvidia: "simple-icons:nvidia",
  zai: Zai,
  elevenlabs: "simple-icons:elevenlabs",
  fishaudio: "simple-icons:fishaudio",
  voyage: "thesvg:voyage",
  baai: "thesvg:baai",
  azure: "mdi:microsoft-azure",
  microsoft: "mdi:microsoft",
  sippulse: () => <BrandLogoSmall class="h-full w-full scale-110 *:text-current" useCurrentColor />,
} as Record<string, string | FlexComponent>;

export function getModelIcon(model?: Model) {
  if (!model) return "lucide:brain";
  const provider = model.provider.trim().toLowerCase();

  return getIconByModelProvider(provider);
}

export function getIconByModelProvider(provider: string): string | FlexComponent {
  return iconsByProvider[provider] || "lucide:brain";
}
